import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { getTransactions } from "@/shared/api";
import { getAuthToken } from "@/utils/auth";
import { produce } from "../utils/immer";

// Use our own interface definition instead of importing from @/types
// Transaction data type to match the API response
export interface TransactionData {
  transactionId: string;
  status: string;
  startDate: string;
  endDate: string;
  sourceStartpoint?: string;
  sourceDataReceivedTime?: string;
  destinationEndpoint?: string;
  destinationDataReceivedTime?: string;
  authorizationType?: string;
  mappingDuration?: number | string;
  sendingDuration?: number | string;
  details?: Array<{
    type?: string;
    content?: any;
    startAt?: string;
    endAt?: string;
  }>;
  payload?: any;
  replayResult?: {
    status: string;
    message?: string;
  };
  [key: string]: any;
}

// Pagination info from API response
export interface PaginationInfo {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Store state type
interface TransactionsState {
  // Map of integrationId -> transactions data
  transactions: Record<string, {
    data: TransactionData[];
    pagination: PaginationInfo;
    lastFetched: number;  // timestamp to track staleness
    fetchParams: {
      startDate?: string;
      endDate?: string;
      page: number;
      pageSize: number;
    };
    error?: string;  // Added error field to store any error messages
  }>;
  isLoading: boolean;
  error: string | null;
}

// Store actions type
interface TransactionsActions {
  fetchTransactions: (
    integrationId: string,
    page?: number,
    pageSize?: number,
    startDate?: string,
    endDate?: string,
    forceRefresh?: boolean
  ) => Promise<TransactionData[]>;
  clearTransactions: (integrationId: string) => void;
  clearAllTransactions: () => void;
  syncTransactionsFromDetailsPage: (integrationId: string, transactions: TransactionData[]) => void;
}

// 15 minutes stale time
const STALE_TIME = 15 * 60 * 1000;

// Debug helper function - keeps the console output clear but informative
const logStoreState = (message: string, data: any) => {
  if (process.env.NODE_ENV !== 'production') {
    console.group(`💾 TransactionStore - ${message}`);
    if (data.currentState) {
      const { transactions } = data.currentState;
      const integrationIds = Object.keys(transactions);
      
      console.log('Store connectors:', integrationIds.length > 0 ? integrationIds : 'None');
      
      // For each connector, show summary instead of full data
      integrationIds.forEach(id => {
        const connData = transactions[id];
        console.log(`Connector ${id}:`, {
          transactionCount: connData.data.length,
          lastFetched: new Date(connData.lastFetched).toISOString(),
          dateRange: connData.fetchParams?.startDate ? 
            `${new Date(connData.fetchParams.startDate).toLocaleString()} → ${new Date(connData.fetchParams.endDate).toLocaleString()}` : 
            'No date range'
        });
        
        // Show sample transaction if available
        if (connData.data.length > 0) {
          console.log('Sample transaction:', {
            id: connData.data[0].transactionId,
            status: connData.data[0].status,
            startDate: connData.data[0].startDate,
            hasDetails: Boolean(connData.data[0].details)
          });
        }
      });
    } else {
      console.log(data);
    }
    console.groupEnd();
  }
};

export const useTransactionsStore = create<TransactionsState & TransactionsActions>()(
  devtools(
    (set, get) => ({
      transactions: {},
      isLoading: false,
      error: null,

      fetchTransactions: async (
        integrationId: string,
        page: number = 1,
        pageSize: number = 250,
        startDate?: string,
        endDate?: string,
        forceRefresh: boolean = false
      ) => {
        // Log the fetch request with structured data
        console.log("TransactionsStore - fetchTransactions called with:", {
          integrationId,
          page,
          pageSize,
          startDate: startDate ? new Date(startDate).toISOString() : undefined,
          endDate: endDate ? new Date(endDate).toISOString() : undefined,
          forceRefresh
        });
        
        // Check if data exists in session storage from details page
        try {
          const detailsPageData = sessionStorage.getItem(`datacord_transactions_${integrationId}`);
          if (detailsPageData && !forceRefresh) {
            console.log(`TransactionsStore - Found data in session storage for integration ${integrationId}`);
            const parsedData = JSON.parse(detailsPageData);
            
            if (Array.isArray(parsedData) && parsedData.length > 0) {
              console.log(`TransactionsStore - Using ${parsedData.length} transactions from session storage`);
              
              // Save to store
              set(
                produce((state: TransactionsState) => {
                  state.transactions[integrationId] = {
                    data: parsedData,
                    pagination: {
                      total: parsedData.length,
                      page: 1,
                      pageSize: parsedData.length,
                      totalPages: 1
                    },
                    lastFetched: Date.now(),
                    fetchParams: {
                      page,
                      pageSize: parsedData.length,
                      startDate,
                      endDate
                    }
                  };
                  state.isLoading = false;
                  state.error = null;
                })
              );
              
              return parsedData;
            }
          }
        } catch (err) {
          console.warn("TransactionsStore - Error reading from session storage:", err);
        }
        
        // Check if we already have fresh data
        const existingData = get().transactions[integrationId];
        const now = Date.now();
        
        // Current state logging
        const currentState = get();
        logStoreState("Current state before fetch", { currentState });
        
        // Determine if we should use cached data
        if (
          !forceRefresh && 
          existingData && 
          (now - existingData.lastFetched < STALE_TIME) &&
          existingData.fetchParams &&
          existingData.fetchParams.startDate === startDate &&
          existingData.fetchParams.endDate === endDate
        ) {
          console.log(`TransactionsStore - Using cached data for integration ${integrationId}, age: ${Math.round((now - existingData.lastFetched) / 1000)}s`);
          return existingData.data;
        }
        
        // Fetch new data
        try {
          set({ isLoading: true, error: null });
          const token = getAuthToken();
          
          if (!token) {
            throw new Error("Authentication token missing");
          }
          
          console.log(`TransactionsStore - Fetching transactions for integration ${integrationId}`, {
            page,
            pageSize,
            startDate,
            endDate,
            endpoint: `/api/analytics/transactions/${integrationId}` // Use the analytics endpoint
          });
          
          const response = await getTransactions(token, integrationId, page, pageSize, startDate, endDate);
          console.log(`TransactionsStore - Received response from API with ${response.transactions.length} transactions`, {
            pagination: response.pagination,
            success: Boolean(response.transactions?.length),
            hasData: Boolean(response && response.transactions),
            dataType: response.transactions ? typeof response.transactions : 'undefined',
            sampleData: response.transactions?.length > 0 ? {
              transactionId: response.transactions[0].transactionId,
              status: response.transactions[0].status || 'unknown',
              startDate: response.transactions[0].startDate || 'unknown',
              endDate: response.transactions[0].endDate || 'unknown',
              hasDetails: Boolean(response.transactions[0].details?.length)
            } : 'No data'
          });
          
          // Validate response data to ensure we're working with valid data
          // If validation fails, throw an error to be handled in the catch block
          if (!response || !Array.isArray(response.transactions)) {
            console.error("TransactionsStore - Invalid API response data:", response);
            throw new Error("Invalid API response data - transactions array missing");
          }
          
          if (response.transactions.length === 0 && page === 1) {
            console.warn(`TransactionsStore - API returned zero transactions for ${integrationId}. This may indicate a filtering issue.`);
          }
          
          // Normalize the data (convert string number fields to actual numbers)
          const normalizedData = response.transactions.map(tx => ({
            ...tx,
            mappingDuration: tx.mappingDuration ? Number(tx.mappingDuration) : undefined,
            sendingDuration: tx.sendingDuration ? Number(tx.sendingDuration) : undefined
          }));
          
          // Save to store
          set(
            produce((state: TransactionsState) => {
              state.transactions[integrationId] = {
                data: normalizedData,
                pagination: response.pagination,
                lastFetched: now,
                fetchParams: {
                  page,
                  pageSize,
                  startDate,
                  endDate
                }
              };
              state.isLoading = false;
            })
          );
          
          // Log updated store state after data was added
          logStoreState(`Updated store for integration ${integrationId}`, { 
            currentState: get(),
            newData: {
              totalTransactions: normalizedData.length,
              updatedAt: new Date(now).toISOString(),
              transactionDates: normalizedData.length > 0 ? {
                firstTransactionDate: normalizedData[0].startDate ? 
                  new Date(normalizedData[0].startDate).toISOString() : 'Unknown',
                lastTransactionDate: normalizedData[normalizedData.length - 1].startDate ?
                  new Date(normalizedData[normalizedData.length - 1].startDate).toISOString() : 'Unknown'
              } : 'No transactions'
            }
          });
          
          return normalizedData;
        } catch (error) {
          console.error("TransactionsStore - Error fetching transactions:", error);
          const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
          
          console.log("TransactionsStore - Creating fallback data for empty response");
          
          // For development/testing: Create fallback mock data if enabled (avoid in production)
          if (process.env.NODE_ENV !== 'production' && window.location.hostname === 'localhost') {
            const mockData = Array(5).fill(0).map((_, i) => ({
              transactionId: `mock-${integrationId}-${i}`,
              status: i % 5 === 0 ? 'error' : (i % 3 === 0 ? 'warning' : 'success'),
              startDate: new Date(Date.now() - (i * 3600000)).toISOString(),
              endDate: new Date(Date.now() - (i * 3600000) + 30000).toISOString(),
              sourceStartpoint: 'API Endpoint',
              destinationEndpoint: 'Message Queue',
              mappingDuration: 150 + Math.random() * 200,
              sendingDuration: 50 + Math.random() * 100,
              details: [{
                type: 'mapping',
                content: { payload: { message: 'Mock data payload' } }
              }]
            }));
            
            console.log("TransactionsStore - Created mock fallback data:", mockData.length);
            
            // Save mock data to store
            set(
              produce((state: TransactionsState) => {
                state.transactions[integrationId] = {
                  data: mockData,
                  pagination: {
                    total: mockData.length,
                    page: 1,
                    pageSize: mockData.length,
                    totalPages: 1
                  },
                  lastFetched: Date.now(),
                  fetchParams: {
                    page,
                    pageSize,
                    startDate,
                    endDate
                  }
                };
                state.isLoading = false;
                state.error = 'Using mock data - API error: ' + errorMessage;
              })
            );
            
            return mockData;
          }
          
          set({ isLoading: false, error: errorMessage });
          return [];
        }
      },

      clearTransactions: (integrationId: string) => {
        set(
          produce((state: TransactionsState) => {
            delete state.transactions[integrationId];
          })
        );
      },

      clearAllTransactions: () => {
        set({ transactions: {} });
      },

      syncTransactionsFromDetailsPage: (integrationId: string, transactions: TransactionData[]) => {
        set(
          produce((state: TransactionsState) => {
            state.transactions[integrationId] = {
              data: transactions,
              pagination: {
                total: transactions.length,
                page: 1,
                pageSize: transactions.length,
                totalPages: 1
              },
              lastFetched: Date.now(),
              fetchParams: {
                page: 1,
                pageSize: transactions.length,
                startDate: transactions[0].startDate,
                endDate: transactions[transactions.length - 1].endDate
              }
            };
            state.isLoading = false;
          })
        );
      }
    }),
    { name: "transactions-store" }
  )
); 